import React from "react";
import { pick } from "@/lib/languages";
import { Link, useLocation } from "react-router-dom";
import { MagnifyingGlass, House, Bed, Ticket } from "@phosphor-icons/react";
import { useSettings } from "@/context/SettingsContext";
import { notFoundL as L } from "@/i18n/common";

/**
 * The page for an address that does not exist.
 *
 * Until now an unknown URL rendered an empty <main> — a white page with a
 * header and a footer and nothing between them, which reads as a broken site
 * rather than a wrong address. Most arrivals here are a mistyped link, a stale
 * bookmark or a search result pointing at a page that moved, and every one of
 * those people came intending to travel. So this offers the three things they
 * were probably looking for rather than an apology and a dead end.
 *
 * The path is echoed back because a person who followed a bad link from
 * somewhere else can then see what was wrong with it.
 */
export default function NotFound() {
  const { pathname } = useLocation();
  const { lang } = useSettings();
  const l = pick(L, lang);

  const routes = [
    { to: "/flights", label: l.flights, icon: Ticket, hint: l.flightsHint },
    { to: "/", label: l.stays, icon: Bed, hint: l.staysHint },
    { to: "/manage", label: l.manage, icon: MagnifyingGlass, hint: l.manageHint },
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 py-14 sm:py-20" data-testid="not-found-page">
      <p className="text-sm font-bold uppercase tracking-wider text-touki-muted">404</p>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-touki-black mt-1">
        {l.title}
      </h1>
      <p className="text-touki-muted mt-2">
        {l.body} <code className="font-mono text-sm break-all">{pathname}</code>. {l.bodyEnd}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-7">
        {routes.map(({ to, label, icon: Icon, hint }) => (
          <Link
            key={to}
            to={to}
            // The card's text would otherwise be read as one long name —
            // "Search flights Compare routes and fares". aria-label names the
            // destination and aria-describedby keeps the hint, so a screen
            // reader gets both, in the right roles, instead of one run-on
            // string.
            aria-label={label}
            aria-describedby={`nf-hint-${to}`}
            className="rounded-lg border border-touki bg-white p-4 hover:border-touki-black transition-colors"
          >
            <Icon size={20} weight="bold" className="text-touki-red" aria-hidden="true" />
            <div className="font-bold text-touki-black mt-2">{label}</div>
            <div id={`nf-hint-${to}`} className="text-sm text-touki-muted mt-0.5">{hint}</div>
          </Link>
        ))}
      </div>

      <Link
        to="/"
        className="inline-flex items-center gap-1.5 mt-7 text-sm font-semibold text-touki-red hover:underline"
      >
        <House size={15} weight="bold" aria-hidden="true" />
        {l.home}
      </Link>
    </div>
  );
}
